import { getApp, getApps, initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import { connectAuthEmulator, getAuth } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const requiredFirebaseConfig = [
  firebaseConfig.apiKey,
  firebaseConfig.authDomain,
  firebaseConfig.projectId,
  firebaseConfig.storageBucket,
  firebaseConfig.messagingSenderId,
  firebaseConfig.appId,
];

export const isFirebaseConfigured = requiredFirebaseConfig.every(Boolean);

const useFirebaseEmulators = import.meta.env.DEV
  && import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true";
const appCheckSiteKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_KEY;

export const isAppCheckConfigured = useFirebaseEmulators || Boolean(appCheckSiteKey);

let emulatorsConnected = false;
let appCheckInitialized = false;

function initializeClientAppCheck(app) {
  if (typeof window === "undefined" || appCheckInitialized) return null;
  if (import.meta.env.DEV) {
    globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN || true;
  }
  const providerKey = appCheckSiteKey
    || (useFirebaseEmulators ? "6Lc_q_YpAAAAAF-Z-Z-Z-Z-Z-Z-Z-Z-Z-Z-Z-Z" : null);
  if (!providerKey) return null;

  const appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(providerKey),
    isTokenAutoRefreshEnabled: true,
  });
  appCheckInitialized = true;
  return appCheck;
}

function connectLocalEmulators({ auth, db, functions, storage }) {
  if (!useFirebaseEmulators || emulatorsConnected) return;

  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  connectStorageEmulator(storage, "127.0.0.1", 9199);
  emulatorsConnected = true;
}

export function getFirebaseServices() {
  if (!isFirebaseConfigured) return null;

  const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  const services = {
    app,
    appCheck: initializeClientAppCheck(app),
    auth: getAuth(app),
    db: getFirestore(app),
    functions: getFunctions(app),
    storage: getStorage(app),
  };

  connectLocalEmulators(services);
  return services;
}
